import type { FormCascadeRule, FormulaVariant, SetMetricEffect } from '../../../config/formCascadeConfig';
import type { MetricColumnConfig } from '../../../config/tpoGenConfig';
import type { PPGRow } from '../../../types/promoMetrics';
import type { RuleContext } from '../../../rules/types';
import { cascadeKPIs, evaluateExpression } from './cascadeKPIs';
import { conditionSources, evaluateCondition } from '../../../rules/engine/evaluateCondition';

/**
 * Pure (no React) helpers that drive the deterministic form -> metric cascade.
 * Both entry points (the change-triggered path in `usePpgData.applyFormCascade`
 * and the init-triggered path at the `initializeFromApi` funnel) resolve the
 * formula and compute the seed through these helpers, so behaviour is identical
 * regardless of how the value got there. The seed is always written through the
 * same `cascadeKPIs` engine the metric DAG uses.
 *
 * Rule gating + formula-variant selection reuse the field-rules engine's
 * `RuleCondition` (`evaluateCondition` / `conditionSources`). Conditions are
 * evaluated against a qualified value map built from the local promo form
 * (`promo:<field>`) and the current PPG row (`metric:<key>`) only.
 */

const EMPTY_CTX: RuleContext = {};

const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === '';

/** A field is empty when its scalar value is empty, or (for range tuples) every part is empty. */
const isFieldEmpty = (raw: unknown): boolean => (Array.isArray(raw) ? raw.every(isEmpty) : isEmpty(raw));

const toNumber = (value: unknown): number =>
    !isEmpty(value) && !isNaN(Number(value)) ? Number(value) : 0;

/** Prefixes promo form values with the `promo:` scope (row-independent; build once per call). */
const buildQualifiedForm = (formValues: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(formValues)) out[`promo:${key}`] = value;
    return out;
};

/** Adds the row's metric values under the `metric:` scope on top of the qualified form map. */
const withRowMetrics = (
    qualifiedForm: Record<string, unknown>,
    rowValues: Record<string, number>,
): Record<string, unknown> => {
    const out: Record<string, unknown> = { ...qualifiedForm };
    for (const [key, value] of Object.entries(rowValues)) out[`metric:${key}`] = value;
    return out;
};

const toQualified = (
    formValues: Record<string, unknown>,
    rowValues?: Record<string, number>,
): Record<string, unknown> =>
    rowValues ? withRowMetrics(buildQualifiedForm(formValues), rowValues) : buildQualifiedForm(formValues);

/**
 * Resolves the expression a rule should evaluate, or `null` when the rule must
 * be skipped, given a pre-built qualified value map:
 * - `rule.when` (if any) does not pass, or
 * - every declared form input is empty (avoids clobbering values with 0), or
 * - no formula resolves for the selected unit / variant.
 */
function resolveRuleFormulaQ(
    rule: FormCascadeRule,
    formValues: Record<string, unknown>,
    qualified: Record<string, unknown>,
): string | null {
    const { effect } = rule;

    if (rule.when && !evaluateCondition(rule.when, qualified, EMPTY_CTX)) return null;

    if (effect.inputs.form.length > 0 && effect.inputs.form.every((field) => isFieldEmpty(formValues[field]))) {
        return null;
    }

    const selected: FormulaVariant | SetMetricEffect =
        effect.variants?.find((variant) => !variant.when || evaluateCondition(variant.when, qualified, EMPTY_CTX)) ??
        effect;

    let formula: string | undefined;
    if (selected.formulaByUnit && selected.unitField) {
        const unit = formValues[selected.unitField];
        if (!isEmpty(unit)) formula = selected.formulaByUnit[String(unit)];
    }
    formula = formula ?? (selected as FormulaVariant).formula ?? effect.defaultFormula;

    return formula ?? null;
}

/** Convenience wrapper that builds the qualified map from raw form + row values. */
export function resolveRuleFormula(
    rule: FormCascadeRule,
    formValues: Record<string, unknown>,
    rowValues?: Record<string, number>,
): string | null {
    return resolveRuleFormulaQ(rule, formValues, toQualified(formValues, rowValues));
}

/**
 * Builds the form-scalar slice of the variable map declared by `rule.effect.inputs.form`.
 * Range-tuple fields are expanded into `<field>_first` / `<field>_second` numeric vars so
 * formulas can reference each part; scalar fields keep their plain name.
 */
const buildFormScalars = (rule: FormCascadeRule, formValues: Record<string, unknown>): Record<string, number> => {
    const scalars: Record<string, number> = {};
    for (const field of rule.effect.inputs.form) {
        const raw = formValues[field];
        if (Array.isArray(raw)) {
            scalars[`${field}_first`] = toNumber(raw[0]);
            scalars[`${field}_second`] = toNumber(raw[1]);
        } else {
            scalars[field] = toNumber(raw);
        }
    }
    return scalars;
};

/**
 * Computes the seed value for a single row, or `null` when the rule is skipped.
 * The variable map merges the row's metric values with the rule's form scalars,
 * then is evaluated with the shared `evaluateExpression`.
 */
export function computeSeedForRow(
    rule: FormCascadeRule,
    rowValues: Record<string, number>,
    formValues: Record<string, unknown>,
): number | null {
    const formula = resolveRuleFormula(rule, formValues, rowValues);
    if (formula === null) return null;
    const vars = { ...rowValues, ...buildFormScalars(rule, formValues) };
    return evaluateExpression(formula, vars);
}

/**
 * The set of promo form fields whose change should re-run the cascade: each
 * rule's explicit `triggers` plus its input/unit fields plus every `promo:`
 * source referenced by its `when` / variant conditions (so gating/selection
 * field changes - e.g. status, tactic - re-evaluate the cascade).
 */
export function cascadeTriggerFields(rules: FormCascadeRule[]): Set<string> {
    const out = new Set<string>();
    for (const rule of rules) {
        rule.triggers.forEach((trigger) => out.add(trigger));
        rule.effect.inputs.form.forEach((field) => out.add(field));
        if (rule.effect.unitField) out.add(rule.effect.unitField);
        rule.effect.variants?.forEach((variant) => {
            if (variant.unitField) out.add(variant.unitField);
        });
        const whens = [rule.when, ...(rule.effect.variants ?? []).map((variant) => variant.when)];
        for (const when of whens) {
            for (const source of conditionSources(when)) {
                if (source.startsWith('promo:')) out.add(source.slice('promo:'.length));
            }
        }
    }
    return out;
}

export interface ApplyFormCascadeOptions {
    /** Limit seeding to these PPG ids (used to seed only recommended/new rows, preserving saved rows). */
    onlyPpgIds?: Set<string>;
    /** When true, seeded keys are added to `userEditedKeys` and `lastCascadedKeys` (dirty + highlighted). */
    markUserEdited?: boolean;
}

/**
 * Applies every resolvable form-cascade rule to the given rows, seeding each
 * rule's metric via `cascadeKPIs` (so downstream dependents cascade). Returns a
 * new `PPGRow[]`; rows are only cloned when at least one rule applies. Operates
 * on top-level rows only, mirroring `usePpgData.handleMetricChange`.
 *
 * The `promo:`-qualified form map is built once per call; each (row, rule) only
 * layers the row's `metric:` values on top for condition evaluation.
 */
export function applyFormCascadeToRows(
    rows: PPGRow[],
    rules: FormCascadeRule[],
    formValues: Record<string, unknown>,
    allConfigs: MetricColumnConfig[],
    opts: ApplyFormCascadeOptions = {},
): PPGRow[] {
    if (rules.length === 0 || rows.length === 0) return rows;

    const qualifiedForm = buildQualifiedForm(formValues);

    return rows.map((row) => {
        if (opts.onlyPpgIds && !opts.onlyPpgIds.has(String(row.ppg_id))) return row;

        let values = row.values;
        let changed = false;
        const editedKeys = opts.markUserEdited ? new Set(row.userEditedKeys) : null;
        const cascadedKeys = opts.markUserEdited ? new Set<string>() : null;

        for (const rule of rules) {
            const formula = resolveRuleFormulaQ(rule, formValues, withRowMetrics(qualifiedForm, values));
            if (formula === null) continue;

            const vars = { ...values, ...buildFormScalars(rule, formValues) };
            const seed = evaluateExpression(formula, vars);
            const result = cascadeKPIs(allConfigs, rule.effect.metricKey, seed, values);
            values = result.values;
            changed = true;
            if (editedKeys) editedKeys.add(rule.effect.metricKey);
            if (cascadedKeys) {
                cascadedKeys.add(rule.effect.metricKey);
                result.cascadedKeys.forEach((key) => cascadedKeys.add(key));
            }
        }

        if (!changed) return row;

        return {
            ...row,
            values,
            ...(editedKeys ? { userEditedKeys: editedKeys } : {}),
            ...(cascadedKeys ? { lastCascadedKeys: cascadedKeys } : {}),
        };
    });
}
